import {
	createSlice,
	createAsyncThunk,
	createEntityAdapter,
	createSelector,
} from "@reduxjs/toolkit";
import {
	approveWorkflow,
	respondWorkflow,
	declineWorkflow,
	delegateWorkflow,
	getWorkflowsByTemplate,
	getWorkflowsByFormId,
	resumeWorkflow,
	submitWorkflow,
	editWorkflow,
	queryWorkflow,
	deleteWorkflow,
	getWorkflowApprovers,
	deleteWorkflowsByResponseFormId,
	rejectByPayroll,
	addWorkflow,
	rejectByCountiesFinance,
	resendPayrollEmail,
} from "api";
import { formStateEdited, statusEdited } from "./formSlice";
import { selectUser } from "./securitySlice";
import {
	fetchWorkflowHistoryByFormId,
	selectAllWorkflowHistory,
} from "./workflowHistorySlice";

const workflowsAdapter = createEntityAdapter();

const initialState = workflowsAdapter.getInitialState({
	status: "idle",
	error: null,
});

export const deleteWorkflowsByFormId = createAsyncThunk(
	"workflow/deleteWorkflowsByFormId",
	async ({ responseFormId }, { rejectWithValue, dispatch }) => {
		try {
			await deleteWorkflowsByResponseFormId({
				responseFormId,
			});
		} catch (error) {
			console.error(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
				status: "",
			});
		}
	}
);

export const fetchWorkflowsByFormId = createAsyncThunk(
	"workflow/fetchWorkflowsByFormId",
	async ({ responseFormId }, { rejectWithValue, dispatch }) => {
		try {
			dispatch(
				workflowsReceived(
					await getWorkflowsByFormId({
						responseFormId,
					})
				)
			);
		} catch (error) {
			console.error(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
				status: "",
			});
		}
	}
);

export const fetchFromTemplate = createAsyncThunk(
	"workflow/fetchFromTemplate",
	async (
		{
			formGuid,
			formDefinitionId,
			code,
			conditions,
			domain,
			organisationHierarchyId,
			organisation,
		},
		{ rejectWithValue, dispatch }
	) => {
		try {
			dispatch(
				workflowsReceived(
					await getWorkflowsByTemplate({
						organisationHierarchyId,
						formGuid,
						formDefinitionId,
						code,
						conditions,
						domain,
						organisation,
					})
				)
			);
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
				status: "",
			});
		}
	}
);

export const fetchApprovers = createAsyncThunk(
	"workflow/fetchApprovers",
	async (
		{ roleId, organisationId, domain },
		{ rejectWithValue, dispatch }
	) => {
		try {
			const response = await getWorkflowApprovers({
				roleId,
				organisationId,
				domain,
			});
			return response;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
				status: "",
			});
		}
	}
);

const newWorkflowTemplate = {
	status: "Pending",
	type: null,
	action: "Add",
	description: null,
};

export const add = createAsyncThunk(
	"workflow/add",
	async (
		{ responseFormId, userId, roleId, type, sequence, comments },
		{ rejectWithValue, dispatch }
	) => {
		try {
			const newWorkflow = {
				...newWorkflowTemplate,
				responseFormId,
				userId,
				roleId,
				type,
				sequence,
			};
			await addWorkflow(newWorkflow, comments);

			//refresh workflows
			await dispatch(
				fetchWorkflowsByFormId({
					responseFormId,
				})
			);
			await dispatch(fetchWorkflowHistoryByFormId({ responseFormId }));
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
				status: "",
			});
		}
	}
);

export const submit = createAsyncThunk(
	"workflow/submit",
	async ({ workflows, comments }, { rejectWithValue, dispatch }) => {
		try {
			await submitWorkflow(workflows, comments);
			//update form state to be pending, since workflows have submitted successfully
			dispatch(statusEdited("Pending"));
			//auto lock a form after submission
			dispatch(formStateEdited({ isLocked: true }));
			//refresh workflows
			const { responseFormId } = workflows[0];
			await dispatch(
				fetchWorkflowsByFormId({
					responseFormId,
				})
			);
			await dispatch(fetchWorkflowHistoryByFormId({ responseFormId }));
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
				status: "",
			});
		}
	}
);

export const approve = createAsyncThunk(
	"workflow/approve",
	async ({ workflow, comments }, { rejectWithValue, dispatch, getState }) => {
		try {
			await approveWorkflow(workflow, comments);
			//refresh workflows
			const { responseFormId } = workflow;
			await dispatch(
				fetchWorkflowsByFormId({
					responseFormId,
				})
			);
			await dispatch(fetchWorkflowHistoryByFormId({ responseFormId }));
			const state = getState();
			const activeWorkflow = selectActiveWorkflow(state);
			//update form state to be approved, since there is no active workflow after this approval
			if (!activeWorkflow) dispatch(statusEdited("Approved"));
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
				status: "",
			});
		}
	}
);

export const respond = createAsyncThunk(
	"workflow/respond",
	async ({ workflow, comments }, { rejectWithValue, dispatch, getState }) => {
		try {
			await respondWorkflow(workflow, comments);
			//refresh workflows
			const { responseFormId } = workflow;
			await dispatch(
				fetchWorkflowsByFormId({
					responseFormId,
				})
			);
			await dispatch(fetchWorkflowHistoryByFormId({ responseFormId }));
			const state = getState();
			const activeWorkflow = selectActiveWorkflow(state);
			//update form state to be approved, since there is no active workflow after this approval
			if (!activeWorkflow) dispatch(statusEdited("Approved"));
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
				status: "",
			});
		}
	}
);

export const decline = createAsyncThunk(
	"workflow/decline",
	async ({ workflow, comments }, { rejectWithValue, dispatch }) => {
		try {
			await declineWorkflow(workflow, comments);

			//change status to declined
			dispatch(statusEdited("Declined"));

			//refresh workflows
			const { responseFormId } = workflow;
			await dispatch(
				fetchWorkflowsByFormId({
					responseFormId,
				})
			);
			await dispatch(fetchWorkflowHistoryByFormId({ responseFormId }));
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
				status: "",
			});
		}
	}
);

export const delegate = createAsyncThunk(
	"workflow/delegate",
	async (
		{ workflow, delegateUserId, comments },
		{ rejectWithValue, dispatch }
	) => {
		try {
			await delegateWorkflow(workflow, delegateUserId, comments);

			//refresh workflows
			const { responseFormId } = workflow;
			await dispatch(
				fetchWorkflowsByFormId({
					responseFormId,
				})
			);
			await dispatch(fetchWorkflowHistoryByFormId({ responseFormId }));
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
				status: "",
			});
		}
	}
);

export const query = createAsyncThunk(
	"workflow/query",
	async (
		{ workflow, delegateUserId, comments },
		{ rejectWithValue, dispatch }
	) => {
		try {
			await queryWorkflow(workflow, delegateUserId, comments);

			//refresh workflows
			const { responseFormId } = workflow;
			await dispatch(
				fetchWorkflowsByFormId({
					responseFormId,
				})
			);
			await dispatch(fetchWorkflowHistoryByFormId({ responseFormId }));
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
				status: "",
			});
		}
	}
);

export const edit = createAsyncThunk(
	"workflow/edit",
	async ({ workflow, comments }, { rejectWithValue, dispatch }) => {
		try {
			await editWorkflow(workflow, comments);
			//refresh workflows
			const { responseFormId } = workflow;
			await dispatch(
				fetchWorkflowsByFormId({
					responseFormId,
				})
			);
			await dispatch(fetchWorkflowHistoryByFormId({ responseFormId }));
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
				status: "",
			});
		}
	}
);

export const deleteAction = createAsyncThunk(
	"workflow/deleteAction",
	async ({ workflow, comments }, { rejectWithValue, dispatch }) => {
		try {
			await deleteWorkflow(workflow, comments);
			//refresh workflows
			const { responseFormId } = workflow;
			await dispatch(
				fetchWorkflowsByFormId({
					responseFormId,
				})
			);
			await dispatch(fetchWorkflowHistoryByFormId({ responseFormId }));
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
				status: "",
			});
		}
	}
);

export const resume = createAsyncThunk(
	"workflow/resume",
	async ({ workflows, comments }, { rejectWithValue, dispatch }) => {
		try {
			await resumeWorkflow(workflows, comments);
			//update form state to be pending, since workflows have submitted successfully
			dispatch(statusEdited("Pending"));

			//refresh workflows
			const { responseFormId } = workflows[0];
			await dispatch(
				fetchWorkflowsByFormId({
					responseFormId,
				})
			);
			await dispatch(fetchWorkflowHistoryByFormId({ responseFormId }));
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
				status: "",
			});
		}
	}
);

export const resendPayrollEmailThunk = createAsyncThunk(
	"workflow/resume",
	async ({ workflow, comments }, { rejectWithValue, dispatch }) => {
		try {
			await resendPayrollEmail(workflow, comments);
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
				status: "",
			});
		}
	}
);

export const rejectPayroll = createAsyncThunk(
	"workflow/rejectPayroll",
	async ({ formId }, { rejectWithValue }) => {
		try {
			await rejectByPayroll({ formId });
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
				status: "",
			});
		}
	}
);

export const rejectCountiesFinance = createAsyncThunk(
	"workflow/rejectCountiesFinance",
	async ({ formId, comment }, { rejectWithValue }) => {
		try {
			await rejectByCountiesFinance({ formId, comment });
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
				status: "",
			});
		}
	}
);

const workflowsSlice = createSlice({
	name: "workflow",
	initialState,
	reducers: {
		workflowsReceived(state, action) {
			workflowsAdapter.setAll(state, action.payload);
		},
		workflowStatusEdited(state, action) {
			state.status = action.payload;
		},
		workflowErrorEdited(state, action) {
			state.error = action.payload;
		},
	},
	extraReducers: {
		RESET_CME: (state, action) => {
			return initialState;
		},
		[deleteWorkflowsByFormId.fulfilled]: (state, action) => {
			return initialState;
		},
		[deleteWorkflowsByFormId.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[fetchWorkflowsByFormId.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchWorkflowsByFormId.fulfilled]: (state, action) => {
			state.status = "succeeded";
		},
		[fetchWorkflowsByFormId.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[fetchFromTemplate.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchFromTemplate.fulfilled]: (state, action) => {
			state.status = "succeeded";
		},
		[fetchFromTemplate.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[submit.pending]: (state, action) => {
			state.status = "loading";
		},
		[submit.fulfilled]: (state, action) => {
			state.status = "submitted";
			//if submit is successful, redirect to success screen
		},
		[submit.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[approve.pending]: (state, action) => {
			state.status = "loading";
		},
		[approve.fulfilled]: (state, action) => {
			state.status = "approved";
		},
		[approve.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[respond.pending]: (state, action) => {
			state.status = "loading";
		},
		[respond.fulfilled]: (state, action) => {
			state.status = "responded";
		},
		[respond.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[decline.pending]: (state, action) => {
			state.status = "loading";
		},
		[decline.fulfilled]: (state, action) => {
			state.status = "declined";
		},
		[decline.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[edit.pending]: (state, action) => {
			state.status = "loading";
		},
		[edit.fulfilled]: (state, action) => {
			state.status = "edited";
		},
		[edit.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[add.pending]: (state, action) => {
			state.status = "loading";
		},
		[add.fulfilled]: (state, action) => {
			state.status = "edited";
		},
		[add.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[deleteAction.pending]: (state, action) => {
			state.status = "loading";
		},
		[deleteAction.fulfilled]: (state, action) => {
			state.status = "deleted";
		},
		[deleteAction.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[resume.pending]: (state, action) => {
			state.status = "loading";
		},
		[resume.fulfilled]: (state, action) => {
			state.status = "submitted";
		},
		[resume.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[delegate.pending]: (state, action) => {
			state.status = "loading";
		},
		[delegate.fulfilled]: (state, action) => {
			state.status = "delegated";
		},
		[delegate.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[query.pending]: (state, action) => {
			state.status = "loading";
		},
		[query.fulfilled]: (state, action) => {
			state.status = "queried";
		},
		[query.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[rejectPayroll.pending]: (state, action) => {
			state.status = "loading";
		},
		[rejectPayroll.fulfilled]: (state, action) => {
			state.status = "rejected";
		},
		[rejectPayroll.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[rejectCountiesFinance.pending]: (state, action) => {
			state.status = "loading";
		},
		[rejectCountiesFinance.fulfilled]: (state, action) => {
			state.status = "queried";
		},
		[rejectCountiesFinance.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[resendPayrollEmailThunk.pending]: (state, action) => {
			state.status = "loading";
		},
		[resendPayrollEmailThunk.fulfilled]: (state, action) => {
			state.status = "sent";
		},
		[resendPayrollEmailThunk.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
	},
});

/*intentionally excluding fetchApprovers, as there's frontend
		logic tied to the status of this slice*/

export const { workflowsReceived, workflowStatusEdited, workflowErrorEdited } =
	workflowsSlice.actions;

/*
SELECTORS
*/
export const { selectAll: selectAllWorkflows, selectById: selectWorkflowById } =
	workflowsAdapter.getSelectors((state) => state.workflows);

export const selectWorkflowsError = (state) => state.workflows.error;

export const selectWorkflowStatus = (state) => state.workflows.status;

export const selectActiveWorkflow = createSelector([selectAllWorkflows], (w) =>
	w.find((w) => w.isActive)
);
export const selectActiveWorkflowQuery = createSelector(
	[selectActiveWorkflow, selectAllWorkflowHistory],
	(w, wh) => {
		if (w && wh) {
			const fromWh = wh.find((wh) => wh.id === w.fromWorkflowHistoryId);
			return fromWh?.action.toString().toLowerCase() === "query"
				? w
				: null;
		}
	}
);

export const selectMyWorkflows = createSelector(
	[selectAllWorkflows, selectUser],
	(workflows, user) => {
		return workflows.filter((w) => w.userId === user.id);
	}
);

export const selectWorkflowTypes = createSelector(
	[selectAllWorkflows],
	(workflows) => {
		return [
			...new Set(
				workflows
					.filter((w) => w.type !== null && w.type !== undefined)
					.map((w) => w.type)
			),
		];
	}
);

export default workflowsSlice.reducer;
